import React from 'react';
import {
  BookOpen, Music, Sparkles, Scroll, Anchor, Heart, ChevronRight, Users
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

const ICONS = { BookOpen, Music, Sparkles, Scroll, Anchor, Heart };

const CATEGORY_LABEL = {
  curated: 'Curated',
  topical: 'Topical',
  book: 'Book Study',
  'long-form': 'Long-form',
};

/**
 * Plan summary card. Used in the plans list, search, and home "continue" rail.
 *
 * @param plan        — plan summary object from /api/reading-plans
 * @param progress    — optional { current_day, completed_days } for active plans
 * @param onClick     — handler when card is tapped
 * @param compact     — true when used inside Home rail (smaller, horizontal)
 */
export default function StudyPlanCard({ plan, progress, onClick, compact = false }) {
  const {
    t
  } = useTranslation();

  const Icon = ICONS[plan.icon] || BookOpen;
  const cover = plan.cover_color || 'var(--theme-accent)';
  const completed = progress?.completed_days?.length || 0;
  const pct = progress ? Math.min(100, Math.round((completed / plan.length_days) * 100)) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group text-left rounded-2xl overflow-hidden transition-all
        ${compact ? 'flex items-center gap-3 p-3 min-w-[260px]' : 'flex flex-col p-5'}
        active:scale-[0.98]
      `}
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        WebkitTapHighlightColor: 'transparent',
      }}
      data-testid={`plan-card-${plan.plan_id}`}
    >
      <div
        className={compact
          ? 'w-12 h-12 rounded-xl flex items-center justify-center shrink-0'
          : 'w-14 h-14 rounded-2xl flex items-center justify-center mb-4'}
        style={{ background: `${cover}22`, border: `1px solid ${cover}44` }}
      >
        <Icon className={compact ? 'h-5 w-5' : 'h-6 w-6'} style={{ color: cover }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md"
            style={{ background: `${cover}1a`, color: cover }}
          >
            {CATEGORY_LABEL[plan.category] || plan.category}
          </span>
          <span className="text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
            {plan.length_days} days
          </span>
          {/* Freshly-built custom plan gets a subtle NEW badge for
              the first 24h so it visually stands out on the home
              rail the moment the build notification lands. */}
          {plan.is_custom && progress?.started_at && !progress?.completed_days?.length && (Date.now() - new Date(progress.started_at).getTime() < 86400000) && (
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid="plan-card-new-badge"
            >
              New
            </span>
          )}
        </div>

        <h3
          className={`font-semibold ${compact ? 'text-sm' : 'text-base'} truncate`}
          style={{ color: 'var(--theme-text)' }}
        >
          {plan.title}
        </h3>

        {!compact && (
          <p
            className="text-xs mt-1 line-clamp-2"
            style={{ color: 'var(--theme-textSecondary)', lineHeight: 1.5 }}
          >
            {plan.short_description}
          </p>
        )}

        {progress ? (
          <div className="mt-3">
            <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'var(--glass-bg-active)' }}>
              <div
                className="h-full transition-all"
                style={{ width: `${pct}%`, background: cover }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
              <span>{t('generated.studyplancard_day_efdb')} {progress.current_day} of {plan.length_days}</span>
              <span>{pct}%</span>
            </div>
          </div>
        ) : !compact && plan.joiner_count > 0 ? (
          <div className="mt-3 flex items-center gap-1 text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
            <Users className="h-3 w-3" />
            <span>{plan.joiner_count.toLocaleString()} {t('generated.studyplancard_on_this_plan_fd77')}</span>
          </div>
        ) : null}
      </div>
      {compact && (
        <ChevronRight className="h-4 w-4 shrink-0 self-center" style={{ color: 'var(--theme-textSecondary)' }} />
      )}
    </button>
  );
}
